import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { Box } from '../../atoms/Box'
import { Text } from '../../atoms/Text'
import { Loading } from '../../atoms/Loading'
import { Button } from '../../atoms/Button'
import {
  ErrorContainer,
  NotAvailable,
  PrintWrapper,
} from '../../molecules/ChartComponents'
import type { HistogramChartRef } from './components'
import type {
  DistributionSectionProps,
  DistributionSectionRef,
} from './DistributionSection.types'
import {
  DistributionChartSection,
  DistributionViewControls,
} from './DistributionSection.styles'
import { DistributionCharts } from './DistributionCharts'
import { getDistributionExports } from './DistributionSection.exports'

const defaultT = (key: string): string => key.split('.').pop() || key

export const DistributionSection = forwardRef<
  DistributionSectionRef,
  DistributionSectionProps
>(
  (
    {
      data,
      loading = false,
      error = null,
      isRental = false,
      priceMode,
      accentColor,
      t = defaultT,
    },
    ref
  ) => {
    const [separate, setSeparate] = useState(false)
    const rooms = useRef<HistogramChartRef>(null)
    const price = useRef<HistogramChartRef>(null)
    const priceM2 = useRef<HistogramChartRef>(null)
    const area = useRef<HistogramChartRef>(null)
    const constructionYear = useRef<HistogramChartRef>(null)
    const listingDuration = useRef<HistogramChartRef>(null)

    useImperativeHandle(ref, () => ({
      exportRoomsChart: () => rooms.current?.exportChart() || null,
      exportPriceChart: () => price.current?.exportChart() || null,
      exportPriceM2Chart: () => priceM2.current?.exportChart() || null,
      exportAreaChart: () => area.current?.exportChart() || null,
      exportConstructionYearChart: () =>
        constructionYear.current?.exportChart() || null,
      exportListingDurationChart: () =>
        listingDuration.current?.exportChart() || null,
      ...getDistributionExports(data, isRental, priceMode, t),
    }))

    if (loading) {
      return (
        <Box style={{ textAlign: 'center', padding: '40px' }}>
          <Loading variant="spinner" size="large" center />
          <Text variant="body1" color="secondary" style={{ marginTop: '16px' }}>
            {t('distribution.states.loading')}
          </Text>
        </Box>
      )
    }
    if (error)
      return (
        <ErrorContainer variant="error">
          <Text variant="body1">{error}</Text>
        </ErrorContainer>
      )
    if (!data || (!data.rooms.length && !data.price.length)) {
      return (
        <NotAvailable
          title={t('distribution.empty.title')}
          description={t('distribution.empty.description')}
        />
      )
    }

    return (
      <PrintWrapper>
        <DistributionChartSection>
          {Boolean(data.categories?.length) && (
            <DistributionViewControls>
              <Button
                size="small"
                variant={separate ? 'secondary' : 'primary'}
                aria-pressed={!separate}
                onClick={() => setSeparate(false)}
              >
                {t('distributionView.combined')}
              </Button>
              <Button
                size="small"
                variant={separate ? 'primary' : 'secondary'}
                aria-pressed={separate}
                onClick={() => setSeparate(true)}
              >
                {t('distributionView.separate')}
              </Button>
            </DistributionViewControls>
          )}
          <DistributionCharts
            data={data}
            isRental={isRental}
            priceMode={priceMode}
            accentColor={accentColor}
            separate={separate}
            chartRefs={{
              rooms,
              price,
              priceM2,
              area,
              constructionYear,
              listingDuration,
            }}
            t={t}
          />
        </DistributionChartSection>
      </PrintWrapper>
    )
  }
)

DistributionSection.displayName = 'DistributionSection'
